import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/core/auth/AuthContext';
import { useWorkOrderEdit } from './hooks/useWorkOrderEdit';
import { EditBasicInfo } from './components/EditBasicInfo';
import { EditAssignment } from './components/EditAssignment';
import { EditAdditionalInfo } from './components/EditAdditionalInfo';
import { EditSidebarSettings } from './components/EditSidebarSettings';
import { PageHeaderNew } from '@/components/ui';

export default function WorkOrderEditPage() {
  const { id, projectId } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const { workOrder, referenceData, isLoading, isSaving, updateWorkOrder } = useWorkOrderEdit(id || '', companyId);

  const [form, setForm] = useState<any>(null);

  useEffect(() => {
    if (workOrder) {
      setForm({
        title: workOrder.title || '',
        description: workOrder.description || '',
        project_id: workOrder.project_id || '',
        lead_user_id: workOrder.lead_user_id || '',
        assigned_team_id: workOrder.assigned_team_id || '',
        planned_start_date: workOrder.planned_start_date ? workOrder.planned_start_date.slice(0, 10) : '',
        planned_end_date: workOrder.planned_end_date ? workOrder.planned_end_date.slice(0, 10) : '',
        estimated_hours: workOrder.estimated_hours || '',
        priority_code: workOrder.priority_code || 'medium',
        work_type: workOrder.work_type || 'construction',
        notes: workOrder.notes || '',
        category_id: workOrder.category_id || '',
        sub_category_id: workOrder.sub_category_id || '',
        collaborator_ids: workOrder.collaborator_ids || [],
      });
    }
  }, [workOrder]);

  function updateField(field: string, value: any) {
    setForm((prev: any) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Vui lòng nhập tên công việc'); return; }

    try {
      // Data sanitization: Convert empty strings to null for numeric/UUID fields
      const sanitizedData = { ...form };
      
      // Numeric fields
      const numericFields = [
        'estimated_hours', 'area_m2', 'tree_count', 
        'lawn_area_m2', 'shrub_area_m2', 'hedge_length_m', 
        'material_quantity', 'progress_percent'
      ];
      
      // UUID/Relation fields
      const relationFields = [
        'lead_user_id', 'assigned_team_id', 'project_id', 
        'category_id', 'sub_category_id', 'phase_id', 'contract_id'
      ];

      // Date fields that cause "invalid input syntax for type timestamp" if sent as ""
      const dateFields = [
        'planned_start_date', 'planned_end_date', 'due_date', 
        'actual_start_date', 'actual_end_date', 'scheduled_date'
      ];

      numericFields.forEach(field => {
        if (sanitizedData[field] === '') {
          sanitizedData[field] = null;
        } else if (sanitizedData[field] !== null && sanitizedData[field] !== undefined) {
          // Ensure it's a number if it has a value
          const num = parseFloat(sanitizedData[field]);
          sanitizedData[field] = isNaN(num) ? null : num;
        }
      });

      relationFields.forEach(field => {
        if (sanitizedData[field] === '') {
          sanitizedData[field] = null;
        }
      });

      dateFields.forEach(field => {
        if (sanitizedData[field] === '') {
          sanitizedData[field] = null;
        }
      });

      await updateWorkOrder(sanitizedData);
      toast.success('Đã cập nhật công việc');
      navigate(`/work-orders/detail/${id}`);
    } catch (err: any) {
      console.error('[WorkOrderEdit] Update failed:', err);
      toast.error(err.message || 'Lỗi khi cập nhật công việc');
    }
  }

  if (isLoading || !form) {
    return (
      <div className="p-4 space-y-4 animate-pulse">
        <div className="h-12 w-1/3 bg-slate-100 rounded-md" />
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-9 space-y-4">
            <div className="h-64 bg-slate-100 rounded-md" />
            <div className="h-48 bg-slate-100 rounded-md" />
          </div>
          <div className="col-span-3 h-96 bg-slate-100 rounded-md" />
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="p-4 space-y-4 bg-slate-50 min-h-screen text-slate-900">
      <PageHeaderNew 
        title="Chỉnh sửa công việc"
        subtitle="Cập nhật thông tin chi tiết cho công việc."
        backUrl={`/work-orders/detail/${id}`}
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        <div className="lg:col-span-9 space-y-4">
          <EditBasicInfo
            form={form}
            projects={referenceData?.projects || []}
            referenceData={referenceData}
            onChange={updateField}
          />
          <EditAssignment
            form={form}
            users={referenceData?.users || []}
            teams={referenceData?.teams || []}
            onChange={updateField}
          />
          <EditAdditionalInfo
            form={form}
            onChange={updateField}
          />
        </div>

        <div className="lg:col-span-3 space-y-4">
          <EditSidebarSettings />
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 pt-6 border-t border-slate-100">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="h-10 px-8 rounded-md border border-slate-200 bg-white sm:text-sm text-xs text-slate-600 hover:bg-slate-50 transition-colors"
        >
          Hủy bỏ
        </button>
        <button
          type="submit"
          disabled={isSaving}
          className="h-10 px-8 flex items-center gap-2 rounded-md bg-emerald-800 sm:text-sm text-xs text-white hover:bg-emerald-900 transition-all -emerald-950/20 disabled:opacity-50"
        >
          {isSaving ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-save-line" />}
          {isSaving ? 'Đang lưu...' : 'Lưu thay đổi'}
        </button>
      </div>
    </form>
  );
}
